import { optionId, optionLabel, formErrorMessage } from "@/lib/formLogic";
// Full-page Custom Form builder — /app/forms/new and /app/forms/:id/edit.
// Reuses the existing form_templates API, field types and filler renderer.
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSafetyNavigate as useNavigate, SafetyLink as Link } from "@/lib/safetyNavigation";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, GripVertical, Trash2, Copy, Eye, Plus, X, Loader2, Layers,
  Smartphone, Tablet, Monitor, ChevronUp, ChevronDown, Briefcase, Type,
} from "lucide-react";
import { toast } from "sonner";
import { FIELD_TYPES, TYPE_LABEL, TYPE_ICON, newFieldId, FormFields } from "./Forms";

const CHOICE = ["radio", "checkbox", "dropdown"];
const HAS_PLACEHOLDER = ["short_text", "long_text", "number"];
const PREVIEW_WIDTHS = [
  { key: "mobile", label: "Mobile", w: 390, icon: Smartphone },
  { key: "tablet", label: "Tablet", w: 744, icon: Tablet },
  { key: "desktop", label: "Desktop", w: 900, icon: Monitor },
];

export default function FormBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isEdit = !!id;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [availability, setAvailability] = useState({ manual: true, assignable: true, field_access: true, job: true, safety: true });
  const [category, setCategory] = useState(() => window.location.pathname.startsWith("/app/health-safety/") ? "safety" : "general");
  const [jobIds, setJobIds] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [fields, setFields] = useState([]);
  const [active, setActive] = useState(true);
  const [shared, setShared] = useState(false);
  const [selected, setSelected] = useState(null); // index of selected question
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [preview, setPreview] = useState(false);
  const [previewW, setPreviewW] = useState("mobile");
  const [previewValues, setPreviewValues] = useState({});
  const dragIdx = useRef(null);

  // Field workers never build forms — bounce them (backend enforces too)
  useEffect(() => { if (user.role === "field") nav("/app/forms", { replace: true }); }, [user.role, nav]);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs((r.data || []).filter((j) => j.status !== "completed"))).catch(() => {});
    if (isEdit) {
      api.get(`/form_templates/${id}`)
        .then((r) => {
          const t = r.data;
          setName(t.name || "");
          setDescription(t.description || "");
          setAvailability({ manual: true, assignable: true, field_access: true, job: true, safety: true, ...t.availability });
          setCategory(t.category || "general");
          setJobIds(t.job_ids || []);
          setActive(t.active !== false);
          setShared(!!t.shared);
          setFields((t.fields || []).map((f) => ({ ...f, options: (f.options || []).map(o=>typeof o === "string"?{id:o,label:o}:o) })));
        })
        .catch(() => { toast.error("Form not found"); nav("/app/forms"); })
        .finally(() => setLoading(false));
    }
  }, [id, isEdit, nav]);

  // Warn on hard navigation with unsaved work
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const touch = () => setDirty(true);
  const setField = (idx, patch) => {
    const original=fields[idx], dependents=fields.flatMap(f=>(f.show_when||[]).filter(r=>r.field_id===original.id));
    if(patch.type && patch.type!==original.type && (dependents.length || original.evidence_rules?.length)){toast.error("Remove dependent conditions and evidence rules before changing this type");return;}
    if(patch.options){const ids=patch.options.map(optionId);if([...dependents,...(original.evidence_rules||[])].some(r=>!ids.includes(r.value))){toast.error("Remove rules referencing this option before deleting it");return;}}
    setFields(arr=>arr.map((f,i)=>i===idx?{...f,...patch}:f));touch();
  };
  const addField = (type) => {
    setFields((arr) => [...arr, {
      id: newFieldId(), type, label: type === "section" ? "New section" : "", required: false,
      placeholder: "", help_text: "", options: CHOICE.includes(type) ? [{id:newFieldId(),label:"Option 1"}] : [],
    }]);
    setSelected(fields.length);
    touch();
  };
  const removeField = idx => {
    let end=idx+1;if(fields[idx].type==='section')while(end<fields.length && fields[end].type!=='section')end++;
    const ids=fields.slice(idx,end).map(f=>f.id);
    if(fields.some(f=>!ids.includes(f.id)&&(f.show_when||[]).some(r=>ids.includes(r.field_id)))){toast.error("Remove dependent conditions before deleting these questions");return;}
    if(end>idx+1 && !window.confirm(`Delete this section and its ${end-idx-1} questions?`))return;
    setFields([...fields.slice(0,idx),...fields.slice(end)]);setSelected(null);touch();
  };
  const duplicateField = idx => {
    let end=idx+1;if(fields[idx].type==='section')while(end<fields.length && fields[end].type!=='section')end++;
    const copies=JSON.parse(JSON.stringify(fields.slice(idx,end))),ids=Object.fromEntries(copies.map(f=>[f.id,newFieldId()]));
    copies.forEach(f=>{f.id=ids[f.id];f.show_when=(f.show_when||[]).map(r=>({...r,field_id:ids[r.field_id]||r.field_id}));});
    setFields([...fields.slice(0,end),...copies,...fields.slice(end)]);setSelected(end);touch();
  };
  const relocate = (from,to) => {
    const next=[...fields]; let end=from+1;
    if(next[from].type==='section')while(end<next.length && next[end].type!=='section')end++;
    if(to>=from && to<end)return;
    const block=next.splice(from,end-from); next.splice(to>from?to-block.length:to,0,...block);
    const seen=new Set();
    for(const f of next){if((f.show_when||[]).some(r=>!seen.has(r.field_id))){toast.error("Move would place a condition before its source question");return;}seen.add(f.id);}
    setFields(next);setSelected(null);touch();
  };
  const moveField = (idx,dir) => {
    if(dir<0 && idx>0){let target=idx-1;if(fields[idx].type==='section')while(target>0 && fields[target].type!=='section')target--;relocate(idx,target);}
    if(dir>0){let end=idx+1;if(fields[idx].type==='section'){while(end<fields.length && fields[end].type!=='section')end++;if(end<fields.length){end++;while(end<fields.length && fields[end].type!=='section')end++;}}else end++;if(end<=fields.length)relocate(idx,end);}
  };
  const onDrop = idx => {const from=dragIdx.current;dragIdx.current=null;if(from!=null)relocate(from,idx);};

  const validate = () => {
    if (!name.trim()) { toast.error("Form name is required"); return false; }
    const questions = fields.filter((f) => f.type !== "section");
    if (questions.length === 0) { toast.error("Add at least one question"); return false; }
    for (const f of fields) {
      if (!f.label.trim()) { toast.error(`Every ${f.type === "section" ? "section" : "question"} needs a label`); return false; }
      if (CHOICE.includes(f.type) && f.options.filter((o) => optionLabel(o).trim()).length === 0) {
        toast.error(`"${f.label}" needs at least one option`); return false;
      }
    }
    return true;
  };

  const save = async (asActive) => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        category, availability,
        job_ids: category === "job" ? jobIds : [],
        fields: fields.map((f) => ({ ...f, units: (f.units||[]).map(u=>u.trim()).filter(Boolean), options: CHOICE.includes(f.type) ? f.options.map(o=>({id:optionId(o),label:optionLabel(o).trim()})) : [] })),
        active: asActive,
      };
      if (isEdit) {
        await api.patch(`/form_templates/${id}`, payload);
        toast.success("Form updated");
      } else {
        await api.post("/form_templates", payload);
        toast.success(asActive ? "Form created — your crew can see it now" : "Draft saved — activate it when you're ready");
      }
      setDirty(false);
      nav("/app/forms");
    } catch (e) {
      toast.error(formErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => { if (dirty) setLeaveOpen(true); else nav("/app/forms"); };

  const questionNumber = (idx) => fields.slice(0, idx + 1).filter((f) => f.type !== "section").length;
  const questionCount = fields.filter((f) => f.type !== "section").length;
  const sel = selected !== null ? fields[selected] : null;

  if (loading) return <div className="p-10 text-sm text-zinc-500 font-mono" data-testid="form-builder-loading">Loading form…</div>;

  return (
    <div data-testid="form-builder-page" className="w-full max-w-none pb-28">
      {/* ---- Sticky header ---- */}
      <div className="sticky top-0 z-30 bg-[var(--background,#fff)] dark:bg-black bg-white border-b-2 border-zinc-900 dark:border-zinc-700 px-4 lg:px-6 py-3">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={goBack} className="text-zinc-500 hover:text-zinc-900 dark:hover:text-white p-1" data-testid="builder-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <div className="text-[10px] font-mono font-bold uppercase tracking-[0.25em] text-zinc-500">
              <Link to="/app/forms" className="hover:text-[#FF5F15]">Custom Forms</Link> / {isEdit ? "Edit form" : "New form"}
            </div>
            <div className="font-display font-black uppercase tracking-tight text-lg truncate flex items-center gap-2">
              {isEdit ? (name || "Edit form") : "Build a custom form"}
              <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 border rounded-sm ${active ? "border-emerald-500 text-emerald-600" : "border-zinc-400 text-zinc-500"}`} data-testid="builder-status-badge">
                <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-zinc-400"}`} />
                {isEdit ? (active ? "Published" : "Draft") : "Draft"}
              </span>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {dirty && <span className="hidden sm:block text-[10px] font-mono uppercase tracking-widest text-amber-600" data-testid="builder-dirty-hint">Unsaved changes</span>}
            <Button variant="outline" onClick={() => { setPreview((p) => !p); setPreviewValues({}); }}
              className={`rounded-sm uppercase tracking-wider font-bold text-xs ${preview ? "border-[#FF5F15] text-[#FF5F15]" : "border-zinc-400"}`}
              data-testid="builder-preview-toggle">
              <Eye className="w-4 h-4 mr-1.5" /> {preview ? "Back to builder" : "Preview"}
            </Button>
            {!isEdit && (
              <Button variant="outline" onClick={() => save(false)} disabled={saving}
                className="rounded-sm uppercase tracking-wider font-bold text-xs border-zinc-400" data-testid="builder-save-draft">
                Save draft
              </Button>
            )}
            <Button onClick={() => save(isEdit ? active : true)} disabled={saving}
              className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="builder-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              {isEdit ? "Save changes" : "Create form"}
            </Button>
          </div>
        </div>
      </div>

      {preview ? (
        /* ---- Preview mode ---- */
        <div className="px-4 lg:px-6 py-6" data-testid="builder-preview-pane">
          <div className="flex items-center justify-center gap-2 mb-5">
            {PREVIEW_WIDTHS.map((p) => {
              const Icon = p.icon;
              return (
                <button key={p.key} onClick={() => setPreviewW(p.key)}
                  className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider border px-3 py-1.5 rounded-sm transition-colors ${previewW === p.key ? "border-[#FF5F15] text-[#FF5F15]" : "border-zinc-300 text-zinc-500 hover:text-zinc-800 dark:hover:text-white"}`}
                  data-testid={`preview-width-${p.key}`}>
                  <Icon className="w-3.5 h-3.5" /> {p.label}
                </button>
              );
            })}
          </div>
          <div className="mx-auto hard-card p-6 transition-all" style={{ maxWidth: PREVIEW_WIDTHS.find((p) => p.key === previewW).w }}>
            <div className="text-[10px] font-mono font-bold uppercase tracking-[0.25em] text-[#FF5F15] mb-1">Preview — what your crew sees</div>
            <div className="font-display font-black uppercase tracking-tight text-2xl">{name || "Untitled form"}</div>
            {description && <p className="text-sm text-zinc-500 mt-1">{description}</p>}
            <div className="space-y-5 mt-6">
              {fields.length === 0 && <p className="text-sm text-zinc-400 italic">No questions yet.</p>}
              <FormFields fields={fields} values={previewValues} onChange={(id,v)=>setPreviewValues(s=>({...s,[id]:v}))} preview />
            </div>
            {fields.length > 0 && (
              <Button disabled className="mt-6 w-full rounded-sm bg-[#FF5F15] text-white uppercase tracking-wider font-bold opacity-60">Submit form</Button>
            )}
          </div>
        </div>
      ) : (
        /* ---- Builder mode ---- */
        <div className="px-4 lg:px-6 py-5 space-y-5">
          {/* Form details */}
          <div className="hard-card p-5" data-testid="builder-details-card">
            <div className="label-tag mb-3">Form details</div>
            <div className="grid lg:grid-cols-[1fr_320px] gap-4">
              <div>
                <Label className="label-tag">Form name *</Label>
                <Input value={name} onChange={(e) => { setName(e.target.value); touch(); }}
                  placeholder="e.g. Daily site inspection, Permit to dig, Excavator pre-start"
                  className="mt-1.5 rounded-sm border-zinc-400" data-testid="builder-name-input" />
              </div>
              <div>
                <Label className="label-tag">Category</Label>
                <Select value={category} onValueChange={(v) => { setCategory(v); touch(); }}>
                  <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400 w-full" data-testid="builder-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General — Forms page</SelectItem>
                    <SelectItem value="safety">Safety — shows in the Safety hub</SelectItem>
                    <SelectItem value="job">Job form — filled against a job, shows on the job page</SelectItem>
                    <SelectItem value="fleet">Fleet & plant — shows on the Fleet page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3">
              <Label className="label-tag">Description (optional)</Label>
              <Textarea rows={2} value={description} onChange={(e) => { setDescription(e.target.value); touch(); }}
                placeholder="Briefly explain when this form should be filled out…"
                className="mt-1.5 rounded-sm border-zinc-400" data-testid="builder-description-input" />
            </div>
            <fieldset className="mt-4 border-t border-zinc-200 dark:border-white/10 pt-3">
              <legend className="text-xs font-bold">Form availability</legend>
              <div className="grid sm:grid-cols-2 gap-2">{[["manual", "Available manually from Forms"], ["assignable", "Can be assigned"], ["field_access", "Allow fieldworker access"], ["job", "Show in Job context when assigned"], ["safety", "Show in Health & Safety (Safety forms only)"]].map(([key, label]) => <label key={key} className="flex items-center gap-2 min-h-11 text-xs"><input type="checkbox" checked={availability[key]} onChange={e => { setAvailability(v => ({ ...v, [key]: e.target.checked })); touch(); }} />{label}</label>)}</div>
            </fieldset>
            {category === "job" && (
              <div className="mt-4 border-t border-zinc-200 dark:border-zinc-700 pt-3.5" data-testid="builder-job-scope">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#FF5F15]">
                  <Briefcase className="w-3.5 h-3.5" /> This form is completed against a Fieldlink job
                </div>
                <label className="flex items-center gap-2 mt-2.5 text-sm font-bold cursor-pointer">
                  <Checkbox checked={jobIds.length === 0} onCheckedChange={(v) => { if (v) { setJobIds([]); touch(); } }} data-testid="builder-alljobs-checkbox" />
                  All jobs (including jobs created later)
                </label>
                <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto">
                  {jobs.map((j) => (
                    <label key={j.id} className="flex items-center gap-2 text-sm cursor-pointer min-w-0">
                      <Checkbox checked={jobIds.includes(j.id)}
                        onCheckedChange={(v) => { setJobIds((p) => (v ? [...p, j.id] : p.filter((x) => x !== j.id))); touch(); }}
                        data-testid={`builder-job-check-${j.id}`} />
                      <span className="truncate">
                        {j.job_number && <span className="font-mono font-bold text-[#FF5F15] mr-1">{j.job_number}</span>}{j.title}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-zinc-500 mt-2">
                  {jobIds.length === 0 ? "Showing on all jobs." : `Showing on ${jobIds.length} selected job${jobIds.length === 1 ? "" : "s"} only.`}
                </p>
              </div>
            )}
          </div>

          {/* Two-column workspace */}
          <div className="flex items-center justify-between">
            <div className="label-tag">Form builder</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500" data-testid="builder-question-count">{questionCount} question{questionCount === 1 ? "" : "s"}</div>
          </div>
          <div className="grid xl:grid-cols-[180px_1fr_360px] lg:grid-cols-[1fr_360px] gap-5 items-start"><nav aria-label="Form structure" className="hidden xl:block space-y-1 sticky top-4">{fields.map((f,i)=><button key={f.id} onClick={()=>setSelected(i)} className={`block text-left text-xs p-2 w-full rounded ${f.type==="section"?"font-bold border-t":""} ${selected===i?"bg-orange-100 dark:bg-orange-950":""}`}>{f.label||"Untitled question"}</button>)}</nav>
            {/* LEFT — question cards */}
            <div className="space-y-2.5 min-w-0" data-testid="builder-question-list">
              {fields.length === 0 && (
                <div className="hard-card p-10 text-center" data-testid="builder-empty-state">
                  <Layers className="w-8 h-8 mx-auto text-[#FF5F15]" />
                  <div className="font-display font-black uppercase tracking-tight text-xl mt-3">Your form is empty</div>
                  <p className="text-sm text-zinc-500 mt-1.5">Add your first field from the panel on the right. Common starting points:</p>
                  <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                    {["short_text", "yes_no", "photo"].map((t) => (
                      <Button key={t} size="sm" variant="outline" onClick={() => addField(t)}
                        className="rounded-sm text-xs font-bold uppercase tracking-wider border-zinc-400" data-testid={`empty-add-${t}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> {TYPE_LABEL[t]}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              {fields.map((f, idx) => {
                const Icon = TYPE_ICON[f.type] || Type;
                const isSection = f.type === "section";
                const isSel = selected === idx;
                return (
                  <div key={f.id}
                    draggable
                    onDragStart={() => { dragIdx.current = idx; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(idx)}
                    onClick={() => setSelected(idx)}
                    className={`hard-card p-3.5 cursor-pointer transition-colors ${isSel ? "border-[#FF5F15] ring-1 ring-[#FF5F15]/40" : "hover:border-zinc-500"} ${isSection ? "border-l-4 border-l-[#FF5F15]" : ""}`}
                    data-testid={`builder-card-${idx}`}>
                    <div className="flex items-center gap-2.5">
                      <span className="cursor-grab text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 shrink-0" title="Drag to reorder" data-testid={`builder-drag-${idx}`}>
                        <GripVertical className="w-4 h-4" />
                      </span>
                      {!isSection && <span className="font-mono font-bold text-xs text-zinc-400 w-6 shrink-0">{String(questionNumber(idx)).padStart(2, "0")}</span>}
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 border shrink-0 text-[9px] font-bold uppercase tracking-wider ${isSection ? "bg-[#FF5F15]/10 border-[#FF5F15] text-[#FF5F15]" : "bg-zinc-100 dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600"}`}>
                        {isSection ? <Layers className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                        {isSection ? "Section" : TYPE_LABEL[f.type]}
                      </span>
                      <span className={`flex-1 truncate text-sm ${isSection ? "font-display font-black uppercase tracking-tight" : "font-bold"} ${f.label ? "" : "text-zinc-400 italic"}`}>
                        {f.label || "Untitled question"}
                      </span>
                      {f.required && !isSection && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-[#FF5F15] shrink-0"><span className="w-1.5 h-1.5 rounded-full bg-[#FF5F15]" /> Required</span>
                      )}
                      <span className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => moveField(idx, -1)} className="text-zinc-400 hover:text-zinc-800 dark:hover:text-white p-1" aria-label="Move up" data-testid={`builder-up-${idx}`}><ChevronUp className="w-3.5 h-3.5" /></button>
                        <button onClick={() => moveField(idx, 1)} className="text-zinc-400 hover:text-zinc-800 dark:hover:text-white p-1" aria-label="Move down" data-testid={`builder-down-${idx}`}><ChevronDown className="w-3.5 h-3.5" /></button>
                        <button onClick={() => duplicateField(idx)} className="text-zinc-400 hover:text-zinc-800 dark:hover:text-white p-1" aria-label="Duplicate" data-testid={`builder-dup-${idx}`}><Copy className="w-3.5 h-3.5" /></button>
                        <button onClick={() => removeField(idx)} className="text-zinc-400 hover:text-red-500 p-1" aria-label="Delete" data-testid={`builder-del-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </span>
                    </div>
                    {!isSection && (f.help_text || f.placeholder) && (
                      <div className="text-[11px] text-zinc-500 mt-1.5 ml-[52px] truncate">{f.help_text || f.placeholder}</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* RIGHT — add field / edit question */}
            <div className="hard-card p-4 lg:sticky lg:top-[76px]" data-testid="builder-side-panel">
              {sel ? (
                <QuestionEditor fields={fields} field={sel} idx={selected}
                  onChange={(p) => setField(selected, p)}
                  onDuplicate={() => duplicateField(selected)}
                  onDelete={() => removeField(selected)}
                  onClose={() => setSelected(null)} />
              ) : (
                <>
                  <div className="label-tag mb-3">Add field</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {FIELD_TYPES.map((t) => {
                      const Icon = t.icon;
                      return (
                        <button key={t.value} onClick={() => addField(t.value)}
                          className="flex items-center gap-2 border border-zinc-300 dark:border-zinc-600 px-2.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider hover:border-[#FF5F15] hover:text-[#FF5F15] transition-colors rounded-sm"
                          data-testid={`add-field-${t.value}`}>
                          <Icon className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{t.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={() => addField("section")}
                    className="mt-3 w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#FF5F15]/50 text-[#FF5F15] px-2.5 py-2.5 text-[11px] font-bold uppercase tracking-wider hover:border-[#FF5F15] hover:bg-[#FF5F15]/5 transition-colors rounded-sm"
                    data-testid="add-section-btn">
                    <Layers className="w-3.5 h-3.5" /> Add section
                  </button>
                  <p className="text-[11px] text-zinc-500 mt-3">Click a question to edit its label, help text, required setting and options. Drag the ⋮⋮ handle to reorder.</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unsaved changes dialog */}
      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="rounded-xl max-w-md" data-testid="builder-leave-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">Unsaved changes</DialogTitle>
            <DialogDescription>You have changes that haven't been saved.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveOpen(false)} className="rounded-sm uppercase tracking-wider font-bold" data-testid="builder-keep-editing">Keep editing</Button>
            <Button onClick={() => nav("/app/forms")} className="rounded-sm bg-red-600 hover:bg-red-700 text-white uppercase tracking-wider font-bold" data-testid="builder-discard">Discard changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuestionEditor({ fields, field, idx, onChange, onDuplicate, onDelete, onClose }) {
  const isSection = field.type === "section";
  const hasOptions = CHOICE.includes(field.type);
  const updateOption = (oi, val) => onChange({ options: field.options.map((o, i) => (i === oi ? {...o,label:val} : o)) });
  return (
    <div data-testid="question-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tag">{isSection ? "Edit section" : "Edit question"}</div>
        <button onClick={onClose} className="text-zinc-400 hover:text-zinc-800 dark:hover:text-white" aria-label="Close editor" data-testid="editor-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="label-tag">{isSection ? "Section title *" : "Question *"}</Label>
          <Input value={field.label} onChange={(e) => onChange({ label: e.target.value })}
            placeholder={isSection ? "e.g. Machine details" : "e.g. Is the work area safe?"}
            className="mt-1.5 rounded-sm border-zinc-400" data-testid="editor-label-input" />
        </div>
        {!isSection && (
          <div>
            <Label className="label-tag">Field type</Label>
            <Select value={field.type}
              onValueChange={(v) => onChange({ type: v, options: CHOICE.includes(v) ? (field.options?.length ? field.options : [{id:newFieldId(),label:"Option 1"}]) : [] })}>
              <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400 w-full" data-testid="editor-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {!isSection && HAS_PLACEHOLDER.includes(field.type) && (
          <div>
            <Label className="label-tag">Placeholder</Label>
            <Input value={field.placeholder || ""} onChange={(e) => onChange({ placeholder: e.target.value })}
              className="mt-1.5 rounded-sm border-zinc-400" data-testid="editor-placeholder-input" />
          </div>
        )}
        <div>
          <Label className="label-tag">{isSection ? "Description" : ["photo", "file", "signature", "location"].includes(field.type) ? "Instructions" : "Help text"}</Label>
          <Textarea rows={2} value={field.help_text || ""} onChange={(e) => onChange({ help_text: e.target.value })}
            className="mt-1.5 rounded-sm border-zinc-400" data-testid="editor-help-input" />
        </div>
        {!isSection && (
          <label className="flex items-center justify-between border border-zinc-200 dark:border-zinc-700 rounded-sm px-3 py-2.5">
            <span className="text-xs font-bold uppercase tracking-wider">Required</span>
            <Switch checked={!!field.required} onCheckedChange={(v) => onChange({ required: v })} data-testid="editor-required-switch" />
          </label>
        )}
        {hasOptions && (
          <div>
            <Label className="label-tag">Options</Label>
            <div className="space-y-1.5 mt-1.5">
              {field.options.map((o, oi) => (
                <div key={oi} className="flex items-center gap-1.5">
                  <Input value={optionLabel(o)} onChange={(e) => updateOption(oi, e.target.value)}
                    className="rounded-sm border-zinc-300 h-8 text-sm" data-testid={`editor-option-${oi}`} />
                  <button onClick={() => onChange({ options: field.options.filter((_, i) => i !== oi) })}
                    className="text-zinc-400 hover:text-red-500 shrink-0" aria-label="Remove option" data-testid={`editor-option-del-${oi}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={() => onChange({ options: [...field.options, {id:newFieldId(),label:`Option ${field.options.length + 1}`}] })}
              className="mt-2 rounded-sm text-[10px] font-bold uppercase tracking-wider border-zinc-400 h-7" data-testid="editor-add-option">
              <Plus className="w-3 h-3 mr-1" /> Add option
            </Button>
          </div>
        )}
        <RuleEditor fields={fields} field={field} idx={idx} onChange={onChange} />
        <div className="border-t border-zinc-200 dark:border-zinc-700 pt-3 grid grid-cols-2 gap-2">
          <Button size="sm" variant="outline" onClick={onDuplicate} className="rounded-sm text-[10px] font-bold uppercase tracking-wider border-zinc-400" data-testid="editor-duplicate">
            <Copy className="w-3 h-3 mr-1" /> Duplicate
          </Button>
          <Button size="sm" variant="outline" onClick={onDelete} className="rounded-sm text-[10px] font-bold uppercase tracking-wider text-red-600 border-red-300 hover:bg-red-50" data-testid="editor-delete">
            <Trash2 className="w-3 h-3 mr-1" /> Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

function RuleEditor({fields,field,idx,onChange}) {
  const choices=f=>f.type==='yes_no'?[{id:true,label:'Yes'},{id:false,label:'No'}]:(f.options||[]).map(o=>({id:optionId(o),label:optionLabel(o)}));
  const sources=fields.slice(0,idx).filter(f=>['yes_no',...CHOICE].includes(f.type));
  const conditions=field.show_when||[],evidence=field.evidence_rules||[];
  const selectClass='w-full border rounded p-2 bg-white dark:bg-zinc-900 text-sm';
  return <div className="space-y-3 border-t pt-3">
    <p className="text-xs font-bold">Visibility · all conditions must match</p>
    {conditions.map((r,i)=>{const source=fields.find(f=>f.id===r.field_id),opts=source?choices(source):[];const patch=p=>onChange({show_when:conditions.map((v,j)=>j===i?{...v,...p}:v)});return <div key={i} className="space-y-2 border rounded p-2">
      <select aria-label="Source question" className={selectClass} value={r.field_id} onChange={e=>{const f=fields.find(f=>f.id===e.target.value);patch({field_id:f.id,operator:f.type==='checkbox'?'contains':'is',value:choices(f)[0]?.id});}}>{sources.map(f=><option key={f.id} value={f.id}>{f.label}</option>)}</select>
      <select aria-label="Condition operator" className={selectClass} value={r.operator} onChange={e=>patch({operator:e.target.value})}>{(source?.type==='checkbox'?['contains','not_contains']:['is','is_not']).map(v=><option key={v} value={v}>{v.replaceAll('_',' ')}</option>)}</select>
      <select aria-label="Condition answer" className={selectClass} value={JSON.stringify(r.value)} onChange={e=>patch({value:JSON.parse(e.target.value)})}>{opts.map(o=><option key={String(o.id)} value={JSON.stringify(o.id)}>{o.label}</option>)}</select>
      <Button variant="outline" size="sm" onClick={()=>onChange({show_when:conditions.filter((_,j)=>j!==i)})}>Remove condition</Button>
    </div>;})}
    <Button variant="outline" size="sm" disabled={!sources.length || conditions.length>=5} onClick={()=>{const f=sources[0];onChange({show_when:[...conditions,{field_id:f.id,operator:f.type==='checkbox'?'contains':'is',value:choices(f)[0]?.id}]});}}>Add condition</Button>
    {['yes_no',...CHOICE].includes(field.type) && <div className="space-y-2"><p className="text-xs font-bold">When answered · explicit evidence and review</p>{choices(field).map(o=>{const rule=evidence.find(r=>r.value===o.id)||{};return <div key={String(o.id)} className="border rounded p-2"><p className="text-sm font-bold">{o.label}</p>{[['comment','Require comment'],['photo','Require photo'],['review','Flag for office review']].map(([key,label])=><label key={key} className="flex gap-2 items-center min-h-9 text-xs"><input type="checkbox" checked={!!rule[key]} onChange={e=>{const next={...rule,value:o.id,[key]:e.target.checked};onChange({evidence_rules:[...evidence.filter(r=>r.value!==o.id),next].filter(r=>r.comment||r.photo||r.review)});}}/>{label}</label>)}</div>;})}</div>}
    {['number','currency','quantity'].includes(field.type) && <div className="space-y-2">{[['minimum','Minimum'],['maximum','Maximum'],['precision','Decimal places (0–8)']].map(([key,label])=><label key={key} className="block text-xs">{label}<Input type="number" value={field[key]??''} onChange={e=>onChange({[key]:e.target.value===''?null:Number(e.target.value)})}/></label>)}{field.type==='quantity'&&<label className="block text-xs">Allowed units (comma separated)<Input value={(field.units||[]).join(',')} onChange={e=>onChange({units:e.target.value.split(',')})}/></label>}</div>}
    {field.type==='photo'&&<label className="block text-xs">Maximum photos (1–20)<Input type="number" min="1" max="20" value={field.max_photos||10} onChange={e=>onChange({max_photos:Number(e.target.value)})}/></label>}
    {field.type==='section'&&<div className="space-y-2"><label className="flex gap-2 text-xs"><input type="checkbox" checked={!!field.repeatable} onChange={e=>onChange({repeatable:e.target.checked})}/>Repeat this section</label>{field.repeatable&&<>{[['min_entries','Minimum entries',0],['max_entries','Maximum entries',20]].map(([key,label,fallback])=><label key={key} className="block text-xs">{label}<Input type="number" min="0" max="50" value={field[key]??fallback} onChange={e=>onChange({[key]:Number(e.target.value)})}/></label>)}<p className="text-xs text-zinc-500">Questions until the next section belong to each entry. Nested repeats are not supported.</p></>}</div>}
  </div>;
}
